// Utility functions for converting user-friendly schedule inputs to cron expressions

/**
 * Convert user-friendly schedule configuration to cron expression
 * @param {object} config - Schedule configuration
 * @param {string} config.frequency - Frequency: 'minute', 'hour', 'day', 'week', 'month', 'custom'
 * @param {string} config.minute - Minute (0-59) for hourly+ frequencies
 * @param {string} config.hour - Hour (0-23) for daily+ frequencies
 * @param {string} config.dayOfWeek - Day of week (0-6, 0=Sunday) for weekly frequency
 * @param {string} config.dayOfMonth - Day of month (1-31) for monthly frequency
 * @param {string} config.customCron - Custom cron expression when frequency is 'custom'
 * @returns {string} Cron expression
 */
export function scheduleToCron(config) {
  const { frequency, minute = '0', hour = '9', dayOfWeek = '1', dayOfMonth = '1', customCron = '' } = config;

  switch (frequency) {
    case 'minute':
      // Every minute
      return '* * * * *';

    case 'hour':
      // Every hour at specified minute
      return `${minute} * * * *`;

    case 'day':
      // Every day at specified time
      return `${minute} ${hour} * * *`;

    case 'week':
      // Every week on specified day and time
      return `${minute} ${hour} * * ${dayOfWeek}`;

    case 'month':
      // Every month on specified day and time
      return `${minute} ${hour} ${dayOfMonth} * *`;

    case 'custom':
      // Use custom cron expression
      return customCron;

    default:
      // Default to every day at 9 AM
      return '0 9 * * *';
  }
}

/**
 * Parse a cron expression into user-friendly configuration
 * @param {string} cronExpression - Cron expression (e.g., '0 9 * * *')
 * @returns {object} Configuration object
 */
export function cronToSchedule(cronExpression) {
  if (!cronExpression || typeof cronExpression !== 'string') {
    return {
      frequency: 'day',
      minute: '0',
      hour: '9',
      dayOfWeek: '1',
      dayOfMonth: '1',
      customCron: ''
    };
  }

  const parts = cronExpression.trim().split(/\s+/);
  if (parts.length !== 5) {
    // Invalid cron, treat as custom
    return {
      frequency: 'custom',
      minute: '0',
      hour: '9',
      dayOfWeek: '1',
      dayOfMonth: '1',
      customCron: cronExpression
    };
  }

  const [minute, hour, dayOfMonth, month, dayOfWeek] = parts;

  // Every minute: * * * * *
  if (minute === '*' && hour === '*' && dayOfMonth === '*' && month === '*' && dayOfWeek === '*') {
    return {
      frequency: 'minute',
      minute: '0',
      hour: '9',
      dayOfWeek: '1',
      dayOfMonth: '1',
      customCron: ''
    };
  }

  // Every hour: X * * * *
  if (minute !== '*' && hour === '*' && dayOfMonth === '*' && month === '*' && dayOfWeek === '*') {
    return {
      frequency: 'hour',
      minute,
      hour: '9',
      dayOfWeek: '1',
      dayOfMonth: '1',
      customCron: ''
    };
  }

  // Every day: X Y * * *
  if (minute !== '*' && hour !== '*' && dayOfMonth === '*' && month === '*' && dayOfWeek === '*') {
    return {
      frequency: 'day',
      minute,
      hour,
      dayOfWeek: '1',
      dayOfMonth: '1',
      customCron: ''
    };
  }

  // Every week: X Y * * Z
  if (minute !== '*' && hour !== '*' && dayOfMonth === '*' && month === '*' && dayOfWeek !== '*') {
    return {
      frequency: 'week',
      minute,
      hour,
      dayOfWeek,
      dayOfMonth: '1',
      customCron: ''
    };
  }

  // Every month: X Y Z * *
  if (minute !== '*' && hour !== '*' && dayOfMonth !== '*' && month === '*' && dayOfWeek === '*') {
    return {
      frequency: 'month',
      minute,
      hour,
      dayOfWeek: '1',
      dayOfMonth,
      customCron: ''
    };
  }

  // Default to custom
  return {
    frequency: 'custom',
    minute: '0',
    hour: '9',
    dayOfWeek: '1',
    dayOfMonth: '1',
    customCron: cronExpression
  };
}

/**
 * Get human-readable description of cron expression
 * @param {string} cronExpression - Cron expression
 * @returns {string} Human-readable description
 */
export function describeCron(cronExpression) {
  const config = cronToSchedule(cronExpression);

  switch (config.frequency) {
    case 'minute':
      return 'Every minute';

    case 'hour':
      return `Every hour at minute ${config.minute}`;

    case 'day':
      return `Every day at ${config.hour.padStart(2, '0')}:${config.minute.padStart(2, '0')}`;

    case 'week': {
      const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
      const dayName = days[parseInt(config.dayOfWeek, 10)] || 'Unknown';
      return `Every ${dayName} at ${config.hour.padStart(2, '0')}:${config.minute.padStart(2, '0')}`;
    }

    case 'month':
      return `Every month on day ${config.dayOfMonth} at ${config.hour.padStart(2, '0')}:${config.minute.padStart(2, '0')}`;

    case 'custom':
      return `Custom: ${config.customCron}`;

    default:
      return cronExpression;
  }
}

/**
 * Validate cron expression format
 * @param {string} cronExpression - Cron expression
 * @returns {object} { valid: boolean, error: string }
 */
export function validateCron(cronExpression) {
  if (!cronExpression || typeof cronExpression !== 'string') {
    return { valid: false, error: 'Cron expression is required' };
  }

  const parts = cronExpression.trim().split(/\s+/);
  if (parts.length !== 5) {
    return { valid: false, error: 'Cron expression must have 5 parts (minute hour day month day-of-week)' };
  }

  const [minute, hour, dayOfMonth, month, dayOfWeek] = parts;

  // Basic validation for each part
  const validatePart = (value, min, max, name) => {
    if (value === '*') return { valid: true };
    if (value.includes(',') || value.includes('-') || value.includes('/')) {
      // Allow ranges, lists, and step values (basic check)
      return { valid: true };
    }
    const num = parseInt(value, 10);
    if (isNaN(num) || num < min || num > max) {
      return { valid: false, error: `${name} must be between ${min} and ${max} or use * for any` };
    }
    return { valid: true };
  };

  const checks = [
    validatePart(minute, 0, 59, 'Minute'),
    validatePart(hour, 0, 23, 'Hour'),
    validatePart(dayOfMonth, 1, 31, 'Day of month'),
    validatePart(month, 1, 12, 'Month'),
    validatePart(dayOfWeek, 0, 6, 'Day of week')
  ];

  for (const check of checks) {
    if (!check.valid) {
      return check;
    }
  }

  return { valid: true };
}
